import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  LuCircleAlert,
  LuCircleCheck,
  LuCircleX,
  LuExternalLink,
  LuGitPullRequest,
  LuLoaderPinwheel,
  LuMessageSquare,
  LuPlay,
  LuRefreshCw,
  LuX,
} from 'react-icons/lu';

import {
  fetchGitReviewerRuns,
  gitReviewerErrorMessage,
  GitReviewerFinding,
  GitReviewerLogEntry,
  GitReviewerManualRunResult,
  GitReviewerManualRunSkipped,
  GitReviewerRun,
  GitReviewerRunStatus,
  normalizeGitReviewerRun,
  triggerGitReviewerRun,
} from '../../shared/api/git-reviewer';
import { auth } from '../../shared/firebase/firebase';
import useSocket from '../../shared/hooks/socket-hook';
import MarkdownRenderer from '../../widgets/MarkdownRenderer';

const STATUS_BADGE: Record<GitReviewerRunStatus, string> = {
  queued: 'badge-ghost',
  running: 'badge-info',
  completed: 'badge-success',
  failed: 'badge-error',
};

const VERDICT_ICON = {
  approve: <LuCircleCheck className="text-success" />,
  request_changes: <LuCircleX className="text-error" />,
  comment: <LuMessageSquare className="text-info" />,
};

const VERDICT_LABEL = {
  approve: 'Approved',
  request_changes: 'Requested changes',
  comment: 'Commented',
};

const FINDING_SEVERITY_BADGE: Record<string, string> = {
  critical: 'badge-error',
  error: 'badge-error',
  major: 'badge-warning',
  warning: 'badge-warning',
  minor: 'badge-info',
  info: 'badge-ghost',
};

const REPO_FORMAT = /^([\w.-]+)\/([\w.-]+)$/;

const parseRepo = (repo: string): { owner: string; repo: string } | null => {
  const match = REPO_FORMAT.exec(repo.trim());
  if (!match) return null;
  return { owner: match[1], repo: match[2] };
};

const shortSha = (sha?: string): string => (sha ? sha.substring(0, 7) : 'the current head SHA');

const prKey = (owner: string, repo: string, prNumber: number): string => `${owner}/${repo}#${prNumber}`;

const toTimestamp = (value: string | number | undefined): number => {
  if (value === undefined) return 0;
  if (typeof value === 'number') return value;
  const numeric = Number(value);
  if (Number.isFinite(numeric)) return numeric;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const formatDateTime = (value?: string | number): string => {
  if (value === undefined) return '—';
  const ts = toTimestamp(value);
  if (!ts) return value === '' ? '—' : String(value);
  return new Date(ts).toLocaleString();
};

const formatLogTime = (value: string | number): string => {
  const ts = toTimestamp(value);
  if (!ts) return String(value);
  return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const findingText = (finding: GitReviewerFinding): string => finding.note || finding.message || '';

const findingSeverityBadge = (severity?: string): string =>
  FINDING_SEVERITY_BADGE[severity?.toLowerCase() || ''] || 'badge-ghost';

const reviewedCommitSha = (run: GitReviewerRun): string | undefined =>
  run.lastReviewedCommitSha || run.headSha || run.commitSha;

const runHasDrift = (run: GitReviewerRun): boolean => {
  const parts = [
    run.resultSummary,
    run.commentBody,
    run.comment,
    ...(run.findings || []).flatMap((finding) => [finding.note, finding.message]),
  ];
  const text = parts.filter(Boolean).join(' ').toLowerCase();
  return text.includes('drift') || text.includes('adr');
};

const pickDefined = <T extends object>(value: T): Partial<T> =>
  Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined)) as Partial<T>;

const mergeLogEntries = (
  current: GitReviewerLogEntry[] | undefined,
  incoming: GitReviewerLogEntry[] | undefined,
): GitReviewerLogEntry[] => {
  const byKey = new Map<string, GitReviewerLogEntry>();
  for (const entry of current || []) {
    byKey.set(`${toTimestamp(entry.ts)}:${entry.message}`, entry);
  }
  for (const entry of incoming || []) {
    byKey.set(`${toTimestamp(entry.ts)}:${entry.message}`, entry);
  }
  return Array.from(byKey.values()).sort((a, b) => toTimestamp(a.ts) - toTimestamp(b.ts));
};

const skipRepoLabel = (skip: GitReviewerManualRunSkipped): string => {
  if (skip.owner && skip.repo) return `${skip.owner}/${skip.repo}`;
  return skip.repo || skip.owner || 'Connected repos';
};

const skipPrKey = (skip: GitReviewerManualRunSkipped): string => {
  if (skip.prNumber && (skip.owner || skip.repo)) {
    return `${skipRepoLabel(skip)}#${skip.prNumber}`;
  }
  return `skip:${skip.reason}`;
};

const formatSkipReason = (skip: GitReviewerManualRunSkipped): string => {
  const raw = skip.reason;
  const lower = raw.toLowerCase();

  if (lower.includes('already reviewed') || lower.includes('already_reviewed')) {
    const shaFromMessage = raw.match(/\b[0-9a-f]{7,40}\b/i)?.[0];
    return `Already reviewed at ${shortSha(shaFromMessage)} — no new review was started.`;
  }
  if (lower.includes('unreadable') || lower.includes('read access') || lower.includes('cannot read')) {
    return 'The connected GitHub identity cannot read this PR, so it was skipped.';
  }
  if (lower.includes('closed')) {
    return 'This PR is closed, so it was skipped.';
  }
  if (lower.includes('missing') && lower.includes('head')) {
    return 'This PR has no readable head commit, so it was skipped.';
  }
  if (lower.includes('queued') || lower.includes('running')) {
    return 'This PR is already queued or running, so it was skipped.';
  }
  return raw;
};

const GitReviewerLogPanel = ({ logs }: { logs?: GitReviewerLogEntry[] }) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const sortedLogs = useMemo(() => {
    if (!logs || logs.length === 0) return [];
    return [...logs].sort((a, b) => toTimestamp(a.ts) - toTimestamp(b.ts));
  }, [logs]);

  const lastTimestamp = sortedLogs.length > 0 ? toTimestamp(sortedLogs[sortedLogs.length - 1].ts) : 0;

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [lastTimestamp, sortedLogs.length]);

  if (!logs || logs.length === 0) {
    return <p className="text-sm opacity-50 italic">No execution logs yet.</p>;
  }

  return (
    <div
      ref={scrollRef}
      className="max-h-64 overflow-y-auto rounded-lg bg-base-200/70 p-3 font-mono text-xs leading-relaxed shadow-sm"
    >
      {sortedLogs.map((log, idx) => (
        <div
          key={`${log.ts}:${idx}`}
          className={`py-0.5 whitespace-pre-wrap break-words ${log.isError ? 'text-error' : 'text-base-content/80'}`}
        >
          <span className="opacity-40 mr-2">{formatLogTime(log.ts)}</span>
          {log.message}
        </div>
      ))}
    </div>
  );
};

export default function GitReviewerPage() {
  const { on, off } = useSocket(auth.currentUser?.uid || '');
  const [runs, setRuns] = useState<GitReviewerRun[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);

  const [skipNotices, setSkipNotices] = useState<GitReviewerManualRunSkipped[]>([]);
  const [isRunAllTriggering, setIsRunAllTriggering] = useState(false);
  const [triggeringPrKey, setTriggeringPrKey] = useState<string | null>(null);
  const [specificRepo, setSpecificRepo] = useState('');
  const [specificPrNumber, setSpecificPrNumber] = useState('');

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    fetchGitReviewerRuns()
      .then((data) => !cancelled && setRuns(data?.runs || []))
      .catch(() => !cancelled && setError('Failed to load reviewer runs.'))
      .finally(() => !cancelled && setIsLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onProgress = (data: Partial<GitReviewerRun> & { id?: string; _id?: string }) => {
      const runId = data.id || data._id || '';
      if (!runId) return;

      setRuns((prev) => {
        const exists = prev.some((r) => r.id === runId);

        if (!exists) {
          return [normalizeGitReviewerRun(data), ...prev];
        }

        const patch = pickDefined({ ...data, id: runId });
        return prev.map((r) => {
          if (r.id !== runId) return r;
          return {
            ...r,
            ...patch,
            logs: data.logs ? mergeLogEntries(r.logs, data.logs) : r.logs,
            findings: data.findings ?? r.findings,
          };
        });
      });
    };

    // The FE contract uses `gitReviewerRunProgress`; earlier MF-66 builds listened on
    // `reviewerRunProgress`. Accept both so a mixed deploy doesn't miss stream events.
    const listener = onProgress as (...args: unknown[]) => void;
    on('reviewerRunProgress', onProgress);
    on('gitReviewerRunProgress', onProgress);
    return () => {
      off('reviewerRunProgress', listener);
      off('gitReviewerRunProgress', listener);
    };
  }, [on, off]);

  const toggleExpand = (id: string) => {
    if (!id) return;
    setExpandedRunId(expandedRunId === id ? null : id);
  };

  const mergeIncomingRuns = (incoming: GitReviewerRun[]) => {
    if (!incoming.length) return;
    setRuns((prev) => {
      const byId = new Map<string, GitReviewerRun>();
      for (const run of prev) {
        byId.set(run.id, run);
      }
      for (const run of incoming) {
        byId.set(run.id, run);
      }
      return Array.from(byId.values()).sort((a, b) => toTimestamp(b.createdAt) - toTimestamp(a.createdAt));
    });
  };

  const clearSkipNoticesForRuns = (incoming: GitReviewerRun[]) => {
    if (!incoming.length) return;
    setSkipNotices((prev) =>
      prev.filter(
        (skip) => !incoming.some((run) => skipPrKey(skip) === `${run.repoOwner}/${run.repoName}#${run.prNumber}`),
      ),
    );
  };

  const mergeSkipNotices = (incoming: GitReviewerManualRunSkipped[]) => {
    if (!incoming.length) return;
    setSkipNotices((prev) => {
      const next = [...prev];
      for (const skip of incoming) {
        const key = skipPrKey(skip);
        const existingIndex = next.findIndex((s) => skipPrKey(s) === key);
        if (existingIndex >= 0) {
          next[existingIndex] = skip;
        } else {
          next.push(skip);
        }
      }
      return next;
    });
  };

  const applyManualResult = (result: GitReviewerManualRunResult): { startedCount: number; skippedCount: number } => {
    const incomingRuns = result.dispatched ?? [];
    const skipped = result.skipped ?? [];

    mergeIncomingRuns(incomingRuns);
    clearSkipNoticesForRuns(incomingRuns);
    mergeSkipNotices(skipped);

    return { startedCount: incomingRuns.length, skippedCount: skipped.length };
  };

  const handleRunSingle = async (owner: string, repo: string, prNumber: number) => {
    setError(null);
    setTriggeringPrKey(prKey(owner, repo, prNumber));
    try {
      const result = await triggerGitReviewerRun({ owner, repo, prNumber });
      const { startedCount, skippedCount } = applyManualResult(result);

      if (skippedCount > 0) {
        const detail = result.skipped.length > 0 ? formatSkipReason(result.skipped[0]) : 'skipped';
        toast.error(`Skipped ${owner}/${repo}#${prNumber}: ${detail}`);
      } else if (startedCount > 0) {
        toast.success(`Review run started for ${owner}/${repo}#${prNumber}.`);
      } else {
        toast.success(`Review run requested for ${owner}/${repo}#${prNumber}.`);
      }
    } catch (err) {
      toast.error(gitReviewerErrorMessage(err) || `Failed to trigger review for ${owner}/${repo}#${prNumber}.`);
    } finally {
      setTriggeringPrKey(null);
    }
  };

  const handleRunAll = async () => {
    setError(null);
    setIsRunAllTriggering(true);
    try {
      const result = await triggerGitReviewerRun({ all: true });
      const { startedCount, skippedCount } = applyManualResult(result);

      if (startedCount > 0 && skippedCount > 0) {
        toast.success(`Run all requested — ${startedCount} started, ${skippedCount} skipped.`);
      } else if (startedCount > 0) {
        toast.success(`Run all requested — ${startedCount} started.`);
      } else if (skippedCount > 0) {
        toast.error(`Nothing started — ${skippedCount} skipped. See skipped details below.`);
      } else {
        toast.success('Run all requested.');
      }
    } catch (err) {
      toast.error(gitReviewerErrorMessage(err) || 'Failed to trigger review runs.');
    } finally {
      setIsRunAllTriggering(false);
    }
  };

  const handleRunExisting = async (run: GitReviewerRun) => {
    const parsed = parseRepo(`${run.repoOwner}/${run.repoName}`);
    if (!parsed) {
      toast.error(`Cannot parse repo "${run.repoOwner}/${run.repoName}" — expected owner/repo.`);
      return;
    }
    await handleRunSingle(parsed.owner, parsed.repo, run.prNumber);
  };

  const handleRunSpecific = async (event: FormEvent) => {
    event.preventDefault();
    const parsed = parseRepo(specificRepo);
    const prNumber = Number(specificPrNumber);
    if (!parsed || !Number.isInteger(prNumber) || prNumber <= 0) {
      setError('Enter a valid owner/repo and PR number to run a review.');
      return;
    }
    await handleRunSingle(parsed.owner, parsed.repo, prNumber);
  };

  const renderVerdict = (run: GitReviewerRun) => {
    if (run.verdict) {
      return (
        <div className="flex items-center gap-1.5 font-medium">
          {VERDICT_ICON[run.verdict]}
          <span>{VERDICT_LABEL[run.verdict]}</span>
        </div>
      );
    }

    // Self-PR fallback check
    const commentBody = run.commentBody || run.comment;
    if (commentBody) {
      if (commentBody.includes('**Verdict: Approve**') || commentBody.includes('**Verdict:** Approve')) {
        return (
          <div className="flex items-center gap-1.5 font-medium">
            {VERDICT_ICON.approve}
            <span>Approved (Self-PR)</span>
          </div>
        );
      }
      if (
        commentBody.includes('**Verdict: Requested changes**') ||
        commentBody.includes('**Verdict:** Requested changes')
      ) {
        return (
          <div className="flex items-center gap-1.5 font-medium">
            {VERDICT_ICON.request_changes}
            <span>Requested changes (Self-PR)</span>
          </div>
        );
      }
    }

    return null;
  };

  return (
    <div className="min-h-screen bg-base-200">
      <div className="p-4 space-y-4">
        <div className="flex items-center gap-2">
          <LuGitPullRequest size={20} className="opacity-60" />
          <span className="text-lg font-semibold">Git Reviewer</span>
        </div>
        <p className="text-sm opacity-50 max-w-2xl">
          Autonomous agent that watches your connected repositories and reviews pull requests.
        </p>

        <div className="card bg-base-100 shadow-card">
          <div className="card-body p-4 gap-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <div className="font-semibold">Manual review run</div>
                <p className="text-sm opacity-70">
                  Trigger an immediate review for one PR, or run every eligible PR across your connected repos now.
                </p>
              </div>
              <button className="btn btn-primary shrink-0" onClick={handleRunAll} disabled={isRunAllTriggering}>
                {isRunAllTriggering ? (
                  <LuLoaderPinwheel className="animate-spin" size={16} />
                ) : (
                  <LuRefreshCw size={16} />
                )}
                Run all eligible now
              </button>
            </div>

            <form
              className="border-t border-base-300 pt-3 flex flex-col sm:flex-row gap-2"
              onSubmit={handleRunSpecific}
            >
              <label className="input input-bordered flex items-center gap-2 flex-1">
                <span className="opacity-50 text-xs">owner/repo</span>
                <input
                  className="grow min-w-0"
                  placeholder="vladyslavpatrick/mflow-fe"
                  value={specificRepo}
                  onChange={(e) => setSpecificRepo(e.target.value)}
                />
              </label>
              <label className="input input-bordered flex items-center gap-2 sm:w-40">
                <span className="opacity-50 text-xs">#</span>
                <input
                  className="grow min-w-0"
                  placeholder="123"
                  type="number"
                  min={1}
                  value={specificPrNumber}
                  onChange={(e) => setSpecificPrNumber(e.target.value)}
                />
              </label>
              <button className="btn shrink-0" type="submit" disabled={triggeringPrKey !== null || isRunAllTriggering}>
                {triggeringPrKey !== null ? (
                  <LuLoaderPinwheel className="animate-spin" size={16} />
                ) : (
                  <LuPlay size={16} />
                )}
                Run review
              </button>
            </form>
          </div>
        </div>

        {skipNotices.length > 0 && (
          <div className="alert alert-warning shadow-card">
            <LuCircleAlert className="shrink-0" size={20} />
            <div className="flex-1 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-sm">Skipped by manual run</span>
                <button className="btn btn-ghost btn-xs" onClick={() => setSkipNotices([])}>
                  <LuX size={14} />
                  Clear
                </button>
              </div>
              {skipNotices.map((skip, idx) => (
                <div key={`${skipPrKey(skip)}:${idx}`} className="text-sm">
                  {(skip.owner || skip.repo) && (
                    <span className="font-mono">
                      {skipRepoLabel(skip)}
                      {skip.prNumber ? `#${skip.prNumber}` : ''}
                    </span>
                  )}
                  <span className="opacity-80">
                    {(skip.owner || skip.repo) && ' — '}
                    {formatSkipReason(skip)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div className="alert alert-error py-2">
            <span className="text-sm">{error}</span>
          </div>
        )}

        <div className="space-y-2">
          {isLoading ? (
            <div className="flex justify-center py-6">
              <LuLoaderPinwheel className="animate-spin opacity-40" size={20} />
            </div>
          ) : runs.length === 0 ? (
            <p className="text-sm opacity-40 py-4 text-center">No reviewer runs yet.</p>
          ) : (
            runs.map((run) => {
              const isExpanded = expandedRunId === run.id;
              const hasDrift = runHasDrift(run);
              const commitSha = reviewedCommitSha(run);
              const runSkips = skipNotices.filter(
                (skip) => skipPrKey(skip) === `${run.repoOwner}/${run.repoName}#${run.prNumber}`,
              );
              const parsedRepo = parseRepo(`${run.repoOwner}/${run.repoName}`);
              const isTriggering = parsedRepo
                ? triggeringPrKey === prKey(parsedRepo.owner, parsedRepo.repo, run.prNumber)
                : false;
              const reviewResultMarkdown = run.resultSummary || run.commentBody || run.comment;
              const hasDistinctComment =
                Boolean(run.commentBody && run.resultSummary) && run.commentBody !== run.resultSummary;

              return (
                <div
                  key={run.id}
                  className={`card bg-base-100 transition ${hasDrift ? 'shadow-glow ring-1 ring-warning/50' : 'shadow-card'}`}
                >
                  <div
                    className="card-body p-4 cursor-pointer hover:bg-base-200/50 transition"
                    onClick={() => toggleExpand(run.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleExpand(run.id);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-medium truncate">
                            {run.repoOwner}/{run.repoName}
                          </span>
                          <span className="opacity-50">#{run.prNumber}</span>
                          <a
                            href={run.prUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline flex items-center gap-1 text-xs ml-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            View PR <LuExternalLink size={12} />
                          </a>
                          {runSkips.length > 0 && (
                            <span className="badge badge-sm badge-warning">manual run skipped</span>
                          )}
                        </div>
                        <p className="text-sm truncate opacity-80">{run.prTitle}</p>
                        <p className="text-xs opacity-40 mt-1">
                          {formatDateTime(run.createdAt)}
                          {commitSha && ` · Commit: ${commitSha.substring(0, 7)}`}
                        </p>
                      </div>

                      <div className="flex items-center gap-3 shrink-0 flex-wrap">
                        {renderVerdict(run)}
                        <button
                          className="btn btn-xs btn-outline"
                          disabled={isTriggering || isRunAllTriggering}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRunExisting(run);
                          }}
                        >
                          {isTriggering ? (
                            <LuLoaderPinwheel className="animate-spin" size={12} />
                          ) : (
                            <LuPlay size={12} />
                          )}
                          Run review
                        </button>
                        <span className={`badge badge-sm ${STATUS_BADGE[run.status]}`}>
                          {run.status === 'running' && <LuLoaderPinwheel className="animate-spin mr-1" size={10} />}
                          {run.status}
                        </span>
                      </div>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="border-t border-base-300 p-4 bg-base-200/30 space-y-4">
                      {run.error && <div className="alert alert-error py-2 text-sm">{run.error}</div>}

                      {runSkips.length > 0 && (
                        <div className="alert alert-warning py-2 text-sm">
                          <LuCircleAlert className="shrink-0" size={16} />
                          <div className="space-y-1">
                            {runSkips.map((skip, idx) => (
                              <div key={idx}>{formatSkipReason(skip)}</div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="space-y-2">
                        <span className="text-xs font-semibold opacity-50 uppercase tracking-wide">Execution Logs</span>
                        <GitReviewerLogPanel logs={run.logs} />
                      </div>

                      {reviewResultMarkdown && (
                        <div className="space-y-2">
                          <span className="text-xs font-semibold opacity-50 uppercase tracking-wide">
                            Review Result
                          </span>
                          <div className="bg-base-100 p-3 rounded-lg shadow-sm text-sm prose prose-sm max-w-none">
                            <MarkdownRenderer markdown={reviewResultMarkdown} />
                          </div>
                        </div>
                      )}

                      {hasDistinctComment && run.commentBody && (
                        <div className="space-y-2">
                          <span className="text-xs font-semibold opacity-50 uppercase tracking-wide">
                            Posted GitHub Comment
                          </span>
                          <div className="bg-base-100 p-3 rounded-lg shadow-sm text-sm prose prose-sm max-w-none">
                            <MarkdownRenderer markdown={run.commentBody} />
                          </div>
                        </div>
                      )}

                      {run.findings && run.findings.length > 0 && (
                        <div className="space-y-2">
                          <span className="text-xs font-semibold opacity-50 uppercase tracking-wide">
                            File Findings
                          </span>
                          <div className="space-y-2">
                            {run.findings.map((finding, idx) => {
                              const text = findingText(finding);
                              if (!text) return null;
                              return (
                                <div
                                  key={`${finding.file}:${idx}`}
                                  className="bg-base-100 p-3 rounded-lg shadow-sm text-sm"
                                >
                                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <span className="font-mono text-xs opacity-70">
                                      {finding.file}
                                      {finding.line ? `:${finding.line}` : ''}
                                    </span>
                                    {finding.severity && (
                                      <span className={`badge badge-sm ${findingSeverityBadge(finding.severity)}`}>
                                        {finding.severity}
                                      </span>
                                    )}
                                  </div>
                                  <MarkdownRenderer markdown={text} />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
